import type { Table } from '@tanstack/react-table';
import { type ReactNode, useLayoutEffect, useRef } from 'react';

export function CollapsableSidebarContainer<T>({
	sidebarCollapsed,
	table,
	onlySticky,
	children,
}: {
	sidebarCollapsed?: boolean;
	table: Table<T>;
	onlySticky?: boolean;
	children: ReactNode;
}) {
	let cols = table.getVisibleLeafColumns();

	if (onlySticky) {
		cols = cols.filter((c) => c.getIsPinned());
	}

	const totalWidth = cols.reduce((t, c) => t + c.getSize(), 0);

	const width =
		sidebarCollapsed === undefined
			? undefined
			: sidebarCollapsed
				? 0
				: totalWidth;

	// We do not want to have a width transition all the time. When resizing
	// columns a transition makes it feel laggy and looks weird. Instead, we only
	// apply the transition when collapsing or expanding.
	const containerRef = useRef<HTMLDivElement>(null);
	const prevSidebarCollapsedRef = useRef(sidebarCollapsed);
	useLayoutEffect(() => {
		if (prevSidebarCollapsedRef.current !== sidebarCollapsed) {
			prevSidebarCollapsedRef.current = sidebarCollapsed;

			const containerElement = containerRef.current;

			if (containerElement) {
				containerElement.style.transition =
					'width 0.3s, min-width 0.3s, max-width 0.3s';

				const timeout = setTimeout(() => {
					containerElement.style.transition = '';
				}, 300);

				return () => clearTimeout(timeout);
			}
		}
	});

	return (
		<div
			ref={containerRef}
			css={{
				height: '100%',
				overflow: 'hidden',
				backgroundImage:
					'linear-gradient(270deg, #0000000a 0, #00000000 0.75rem)',
			}}
			style={{ width, minWidth: width, maxWidth: width }}
		>
			<div
				// Prevent content layout shifts when sidebar is collapsed by having an
				// inner div that is always at the expanded width.
				css={{
					height: '100%',
					position: 'relative',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
				}}
				style={{
					width: totalWidth,
					minWidth: totalWidth,
					maxWidth: totalWidth,
				}}
			>
				{children}
			</div>
		</div>
	);
}
